import { z } from 'zod'
import { asObjectType } from '@gqloom/zod'

export const STAFF_IDLE_TIMEOUT_MINUTES_OPTIONS = [5, 10, 15, 30, 60, 120, 240, 480] as const
export const STAFF_MAX_SESSION_HOURS_OPTIONS = [8, 12, 24, 168] as const

const idleTimeoutMinutesSchema = z
  .int()
  .refine(
    (minutes) => (STAFF_IDLE_TIMEOUT_MINUTES_OPTIONS as readonly number[]).includes(minutes),
    'Unsupported idle timeout'
  )

const maxSessionHoursSchema = z
  .int()
  .refine(
    (hours) => (STAFF_MAX_SESSION_HOURS_OPTIONS as readonly number[]).includes(hours),
    'Unsupported maximum session length'
  )

const staffSessionPolicyShape = {
  brandIdleTimeoutMinutes: idleTimeoutMinutesSchema
    .nullable()
    .describe('Sign staff out after this many idle minutes on brand-level pages; null for never'),
  allowLocationIdleTimeout: z
    .boolean()
    .describe("Whether a location's own auto-logout setting may apply on its pages"),
  maxSessionHours: maxSessionHoursSchema
    .nullable()
    .describe('Force a fresh sign-in once a session is this many hours old; null for never'),
}

export const staffSessionPolicySchema = z.object(staffSessionPolicyShape).register(asObjectType, {
  name: 'StaffSessionPolicy',
  description: "The brand's staff session rules: idle timeout and maximum session length",
})

export const staffSessionPolicyInputSchema = z.object(staffSessionPolicyShape)

export const DEFAULT_STAFF_SESSION_POLICY: StaffSessionPolicy = {
  brandIdleTimeoutMinutes: null,
  allowLocationIdleTimeout: true,
  maxSessionHours: null,
}

export function parseStaffSessionPolicy(raw: unknown): StaffSessionPolicy {
  const value = typeof raw === 'string' ? safeJsonParse(raw) : raw
  const parsed = staffSessionPolicyInputSchema.safeParse(value)
  return parsed.success ? parsed.data : DEFAULT_STAFF_SESSION_POLICY
}

function safeJsonParse(raw: string): unknown {
  try {
    return JSON.parse(raw)
  } catch {
    return null
  }
}

export function resolveEffectiveIdleTimeoutMinutes(params: {
  policy: StaffSessionPolicy
  isLocationPage: boolean
  locationAutoLogoutMinutes: number | null
}): number | null {
  const { policy, isLocationPage, locationAutoLogoutMinutes } = params
  const locationMinutes =
    isLocationPage && policy.allowLocationIdleTimeout ? locationAutoLogoutMinutes : null
  const candidates = [policy.brandIdleTimeoutMinutes, locationMinutes].filter(
    (minutes): minutes is number => typeof minutes === 'number' && minutes > 0
  )
  return candidates.length > 0 ? Math.min(...candidates) : null
}

export type StaffSessionPolicy = z.infer<typeof staffSessionPolicyInputSchema>
